import { memo, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from '@/hooks/redux'
import Required from '@/components/required-indicator'
import useChatCount from '@/hooks/use-chat-count'
import { colors, fontWeights } from '@/styles'
import { toUnit } from '@/utils/styles'
import { CssUnit } from '@/types'

type Position = {
  top?: CssUnit
  right?: CssUnit
  left?: CssUnit
}

type Margin = {
  marginLeft?: CssUnit
  marginRight?: CssUnit
}

const MobileIndicator = styled(Required)<Omit<Position, 'left'>>`
  position: absolute;
  right: ${props => toUnit(props.right || 0)};
  top: ${props => toUnit(props.top || 0)};
`

const Wrapper = styled.div<Margin>`
  position: relative;
  margin-left: ${props => toUnit(props.marginLeft || 0)};
  margin-right: ${props => toUnit(props.marginRight || 0)};
`

const Balloon = styled.span<Position & { large?: boolean }>`
  position: absolute;
  right: ${props => toUnit(props.right || 0)};
  left: ${props => toUnit(props.left || 0)};
  top: ${props => toUnit(props.top || 0)};
  background-color: ${colors.primary};
  border-radius: 15px;
  height: 16px;
  min-width: 16px;
  width: fit-content;
  line-height: 16px;
  text-align: center;
  color: ${colors.white};
  font-size: 10px;
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.2px;
  padding: 0 3px;

  ${props =>
    props.large &&
    `
      height: 20px;
      min-width: 20px;
      line-height: 20px;
      font-size: 12px;
      font-weight: ${fontWeights.bold};
      padding: 0 5px;
    `}
`

const truncateNumber = (n: number) => (n > 99 ? '99+' : n.toString())

type ChatNotificationProps = Position &
  Margin & {
    load?: boolean
    showCount?: boolean
    large?: boolean
    children?: React.ReactNode
  }

const ChatNotification: React.FC<ChatNotificationProps> = ({
  showCount = true,
  load = false,
  top,
  right = 'unset',
  left = 'unset',
  marginLeft,
  marginRight,
  large,
  children,
  ...rest
}) => {
  const dispatch = useDispatch()
  const unreadCount = useChatCount()

  useEffect(() => {
    if (!load) return
    dispatch.chat.checkUnreadMessages()
  }, [dispatch.chat, load])

  if (!unreadCount && !children) return null

  return (
    <>
      {unreadCount ? (
        <Wrapper marginLeft={marginLeft} marginRight={marginRight} {...rest}>
          {showCount ? (
            <Balloon large={large} top={top} right={right} left={left}>
              {truncateNumber(unreadCount)}
            </Balloon>
          ) : (
            <MobileIndicator top={top} right={right} />
          )}
        </Wrapper>
      ) : (
        <>{children}</>
      )}
    </>
  )
}

export default memo(ChatNotification)
